import { useMemo } from "react";

import times from "lodash-es/times";

import { Summary } from "src/base-components/Summary";
import { Skeleton } from "src/design-system/Skeleton";
import { useFeatureQueries, useFeatures } from "src/featureCatalogue/queries";

const FeatureQueryMetricsSkeleton: React.FC = () => (
  <div className="flex min-w-0 flex-1 flex-col gap-y-1 px-5 py-4">
    <Skeleton height="h-5.5" width="w-10" />
    <Skeleton height="h-5.5" width="w-50" />
  </div>
);

export const FeatureQueryMetrics = () => {
  const { data: featureQueries, isLoading } = useFeatureQueries();
  const { data: features, isLoading: isLoadingFeatures } = useFeatures();

  const featureMetrics = useMemo(() => {
    const liveFeatures =
      features?.filter(
        (feature) =>
          feature.query?.environment_settings?.live?.status === "active",
      ) || [];

    const sandboxFeatures =
      features?.filter(
        (feature) =>
          feature.query?.environment_settings?.sandbox?.status === "active",
      ) || [];

    return {
      liveFeatures: liveFeatures.length,
      sandboxFeatures: sandboxFeatures.length,
    };
  }, [features]);

  const featureQueryMetrics = useMemo(() => {
    const activeLiveQueries =
      featureQueries?.filter(
        (query) => query.environment_settings?.live?.status === "active",
      ) || [];
    const activeSandboxQueries =
      featureQueries?.filter(
        (query) => query.environment_settings?.sandbox?.status === "active",
      ) || [];
    return {
      activeLiveQueries: activeLiveQueries.length,
      activeSandboxQueries: activeSandboxQueries.length,
    };
  }, [featureQueries]);

  return (
    <Summary dataLoc="feature-metrics-summary" variant="simple">
      {isLoading || isLoadingFeatures ? (
        times(4, (i) => <FeatureQueryMetricsSkeleton key={i} />)
      ) : (
        <>
          <Summary.Cell
            dataLoc="feature-metrics-active-queries-sandbox"
            title="Active Queries in Sandbox Data"
            value={featureQueryMetrics.activeSandboxQueries}
          />
          <Summary.Cell
            dataLoc="feature-metrics-active-queries-live"
            title="Active Queries in Live Data"
            value={featureQueryMetrics.activeLiveQueries}
          />
          <Summary.Cell
            dataLoc="feature-metrics-active-features-sandbox"
            title="Active Features on Sandbox Data"
            value={featureMetrics.sandboxFeatures}
          />
          <Summary.Cell
            dataLoc="feature-metrics-active-features-live"
            title="Active Features on Live Data"
            value={featureMetrics.liveFeatures}
          />
        </>
      )}
    </Summary>
  );
};
